import * as React from 'react';
import ReactCSSTransitionReplace from 'react-css-transition-replace';
import { stripHtml } from "string-strip-html";

import { GRAMMAR, RUNON, RULES_BASED_3, SPELLING, EVIDENCE_SUBOPTIMAL_SPELLING_OR_GRAMMAR_FINAL_ATTEMPT_FEEDBACK, } from '../../../../constants/evidence';
import useFocus from '../../../Shared/hooks/useFocus';
import { EXTRA_SMALL_ICON_BASE_SRC, SMALL_ICON_BASE_SRC, } from '../../../Shared/utils/constants';
import { DEFAULT_HEADER, feedbackHeader, finalAttemptHeader, } from '../../libs/feedbackHeader';
import { isFullOptimal } from '../../libs/optimalSignals';
import { stripLeadingNiceWork } from '../../libs/stringFormatting';

const reviseSrc = `${SMALL_ICON_BASE_SRC}/revise.svg`
const reviewCorrectSrc = `${SMALL_ICON_BASE_SRC}/check-circle.svg`
const plusIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/plus.svg`
const dashIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/dash.svg`
const closeIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/clear-enabled.svg`
const informationSrc = `${window.postbuildEnv.CDN_URL}/images/pages/evidence/icons-information-small.svg`

const reportAProblemOptions = (optimal) => ([
  "I don't think this feedback applies to what I wrote",
  "I don't understand this feedback",
  optimal ? "I think my answer still has an error" : "I think my answer is correct"
])

const ReportAProblemOption = ({ option, handleSelectProblem, }) => {
  function handleClick() { handleSelectProblem(option) }

  return <button className="report-a-problem-option focus-on-light" onClick={handleClick} type="button">{option}</button>
}

const madeLastAttemptAndItWasSuboptimal = (submittedResponses, prompt, lastSubmittedResponse) => {
  const madeLastAttempt = submittedResponses.length === prompt.max_attempts
  return madeLastAttempt && !lastSubmittedResponse.optimal
}

const feedbackToShow = (lastSubmittedResponse, submittedResponses, prompt, customFeedback) => {

  if (customFeedback) { return customFeedback }

  if (!madeLastAttemptAndItWasSuboptimal(submittedResponses, prompt, lastSubmittedResponse)) { return lastSubmittedResponse.feedback }

  if ([GRAMMAR, RUNON, SPELLING, RULES_BASED_3].includes(lastSubmittedResponse.feedback_type)) {
    return EVIDENCE_SUBOPTIMAL_SPELLING_OR_GRAMMAR_FINAL_ATTEMPT_FEEDBACK
  }

  return prompt.max_attempts_feedback
}

const feedbackForInnerHTML = (feedback) => {
  return {__html: feedback}
}

const Feedback: React.SFC = ({ lastSubmittedResponse, prompt, submittedResponses, customFeedback, customFeedbackHeading, customFeedbackKey, reportAProblem, }: any) => {
  const { entry, optimal, hint, highlight, } = lastSubmittedResponse
  const [reportAProblemExpanded, setReportAProblemExpanded] = React.useState(false)
  const [reportSubmitted, setReportSubmitted] = React.useState(false)
  const [hintExpanded, setHintExpanded] = React.useState(false)
  const [containerRef] = useFocus()

  React.useEffect(() => {
    setReportAProblemExpanded(false)
    setReportSubmitted(false)
    setHintExpanded(false)
  }, [lastSubmittedResponse])

  React.useEffect(() => {
    if (reportAProblemExpanded) {
      const el = document.getElementsByClassName("report-a-problem-section")[0]
      el.scrollIntoView(false)
    }
  }, [reportAProblemExpanded])

  function toggleReportAProblemExpanded() { setReportAProblemExpanded(!reportAProblemExpanded) }

  function toggleHintExpanded() { setHintExpanded(!hintExpanded) }

  function handleSelectProblem(report) {
    const { text, } = prompt
    const entryWithoutStem = (entry || '').replace(text, '').trim()
    const callback = () => setReportSubmitted(true)

    reportAProblem({ report, callback, entry: entryWithoutStem, })
  }

  let className = 'feedback'
  let imageSrc = reviseSrc
  let imageAlt = 'Revise icon'
  if (optimal) {
    className += ' optimal'
    imageSrc = reviewCorrectSrc
    imageAlt = 'Check icon'
  }

  const key = customFeedbackKey || submittedResponses.length
  const rawFeedback = feedbackToShow(lastSubmittedResponse, submittedResponses, prompt, customFeedback)
  const feedback = !customFeedback && isFullOptimal(lastSubmittedResponse) ? stripLeadingNiceWork(rawFeedback) : rawFeedback
  let header = DEFAULT_HEADER
  if (!customFeedback) {
    header = madeLastAttemptAndItWasSuboptimal(submittedResponses, prompt, lastSubmittedResponse)
      ? finalAttemptHeader(lastSubmittedResponse, prompt)
      : feedbackHeader(lastSubmittedResponse, prompt)
  }

  const reportAProblemButton = <button className={`report-a-problem-button quill-button borderless medium ${optimal ? 'grey' : 'gold'} focus-on-light`} onClick={toggleReportAProblemExpanded} type="button">Report a problem</button>
  let reportAProblemSection = <span />
  if (reportAProblemExpanded) {
    const reportAProblemOptionElements = reportSubmitted ? null : <div className="options">{reportAProblemOptions(lastSubmittedResponse.optimal).map(opt => <ReportAProblemOption handleSelectProblem={handleSelectProblem} key={opt} option={opt} />)}</div>
    const label = reportSubmitted ? 'Thank you for your feedback!' : 'What did you notice?'
    let text = ''
    if(reportSubmitted && optimal) {
      text = 'Thank you for your feedback! For now, move on to the next part of the activity.'
    } else if(reportSubmitted) {
      text = 'For now, please try your best to revise and improve your sentence.'
    }

    reportAProblemSection = (<section className="report-a-problem-section">
      <div className="report-a-problem-section-header">
        <span className="label">{label}</span>
        <button className="interactive-wrapper focus-on-light" onClick={toggleReportAProblemExpanded} type="button">
          <img alt="Close" src={closeIconSrc} />
          <span>Close</span>
        </button>
      </div>
      <span className="text">{text}</span>
      {reportAProblemOptionElements}
    </section>)
  }

  let headsUpSection

  if (madeLastAttemptAndItWasSuboptimal(submittedResponses, prompt, lastSubmittedResponse)) {
    headsUpSection = (
      <div className="heads-up">
        <div className="label-section">
          <img alt="Information icon" src={informationSrc} />
          <p>Heads up</p>
        </div>
        <p>Our feedback bot is not perfect, and it sometimes might be wrong. If you think the feedback is inaccurate, please click on the “Report a problem” button above.</p>
      </div>
    )
  }

  const hasHint = !!(hint && hint.id)
  const hintId = `hint-${key}`
  let hintButton
  let hintContent

  if (hasHint) {
    const { explanation, image_alt_text, image_link, } = hint
    // The CMS editor saves a cleared explanation as markup like <br>, which still drew an empty box.
    const hasExplanation = !!stripHtml(explanation || '').result.trim()

    hintButton = (
      <button
        aria-controls={hintExpanded ? hintId : undefined}
        aria-expanded={hintExpanded}
        className={`show-hint-button quill-button contained medium ${optimal ? '' : 'gold'} focus-on-light`}
        onClick={toggleHintExpanded}
        type="button"
      >
        <img alt="" src={hintExpanded ? dashIconSrc : plusIconSrc} />
        {hintExpanded ? 'Hide Hint' : 'See Hint'}
      </button>
    )

    if (hintExpanded) {
      hintContent = (
        <div className="feedback-hint" id={hintId}>
          {hasExplanation && <div className="hint-explanation" dangerouslySetInnerHTML={{ __html: explanation }} />}
          {image_link && (
            <div className="hint-image-frame">
              <img alt={image_alt_text || ''} className="hint-image" src={image_link} />
            </div>
          )}
        </div>
      )
    }
  }

  let screenreaderPassageHighlightText
  let screenreaderResponseHighlightText

  const passageHighlights = highlight && highlight.filter(h => h.type === 'passage').map(h => stripHtml(h.text).result)
  const responseHighlights = highlight && highlight.filter(h => h.type === 'response').map(h => stripHtml(h.text).result)

  if (passageHighlights && passageHighlights.length) {
    screenreaderPassageHighlightText = <p className="sr-only">Screenreader users, the feedback you just heard is referring to the following section(s) of the passage: {passageHighlights.join('; ')}</p>
  }

  if (responseHighlights && responseHighlights.length) {
    screenreaderResponseHighlightText = <p className="sr-only">Screenreader users, the feedback you just heard is referring to the following word(s) of your response: {responseHighlights.join('; ')}</p>
  }

  return (
    <div aria-atomic={true} aria-live="assertive" className={`feedback-section ${reportAProblemExpanded ? 'expanded' : ''}`} role="alert">
      <ReactCSSTransitionReplace
        transitionEnterTimeout={1000}
        transitionLeaveTimeout={400}
        transitionName="fade"
      >
        <React.Fragment>
          <div className={className} key={key}>
            <div className="label-section">
              <img alt={imageAlt} src={imageSrc} />
              {/* A heading, not a paragraph: it names the skill the feedback below is about, and
                  is the only thing giving this section a place in the page outline. */}
              <h2>{customFeedbackHeading || header}</h2>
            </div>
            <div className="feedback-wrapper" ref={containerRef} tabIndex={-1}>
              <p className="feedback-text" dangerouslySetInnerHTML={feedbackForInnerHTML(feedback)} />
              {screenreaderPassageHighlightText}
              {screenreaderResponseHighlightText}
            </div>
            <div className="feedback-button-row">
              {hintButton}
              {reportAProblemButton}
            </div>
            {hintContent}
          </div>
          {reportAProblemSection}
        </React.Fragment>
      </ReactCSSTransitionReplace>
      {headsUpSection}
    </div>
  )
}

export default Feedback
